import React, { useId } from "react";
import {
  TableContainer,
  TableWrapper,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  PaginationWrapper,
  PaginationButton,
  CellSeparator
} from "./CustomTableStyles";
import useEmpresa from "../../hooks/useEmpresa";
import CheckRender from "../Common/CheckRender";
import Colors from "../../themes/Colors";

const CustomTable = ({
  columns,
  dataSource,
  pageSize,
  headerColor,
  textHeaderColor,
  paginationColor,
  showPagination,
  hideHeader,
  cellColor,
  width,
  height,
  maxWidth,
  maxHeight,
  minHeight,
  minWidth,
  isServerSidePagination = false,
  serverSideCurrentPage = 1,
  serverSideTotalPages = 1,
  onPageChange
}) => {
  const [currentPage, setCurrentPage] = React.useState(1);

  const empresa = useEmpresa();

  const id = useId();

  const handlePageChange = (pageNumber) => {
    if (isServerSidePagination && onPageChange) {
      onPageChange(pageNumber);
    } else {
      setCurrentPage(pageNumber);
    }
  };

  const paginationData = React.useMemo(() => {
    if (isServerSidePagination) {
      // For server-side pagination, use all data from dataSource
      return {
        totalItems: dataSource?.length || 0,
        validPageSize: pageSize > 0 ? pageSize : 10,
        totalPages: serverSideTotalPages || 1,
        currentItems: Array.isArray(dataSource) ? dataSource : [],
      };
    }

    // Client-side pagination logic
    const totalItems = Array.isArray(dataSource) ? dataSource.length : 0;
    const validPageSize = pageSize > 0 ? pageSize : 10;
    const totalPages = Math.max(1, Math.ceil(totalItems / validPageSize));
    const indexOfLastItem = currentPage * validPageSize;
    const indexOfFirstItem = indexOfLastItem - validPageSize;
    const currentItems = Array.isArray(dataSource)
      ? dataSource.slice(indexOfFirstItem, indexOfLastItem)
      : [];

    return {
      totalItems,
      validPageSize,
      totalPages,
      indexOfLastItem,
      indexOfFirstItem,
      currentItems,
    };
  }, [dataSource, pageSize, currentPage, isServerSidePagination, serverSideTotalPages]);

  const { totalPages, currentItems } = paginationData;
  const activePage = isServerSidePagination ? serverSideCurrentPage : currentPage;

  React.useEffect(() => {
    if (!isServerSidePagination && currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage, isServerSidePagination]);

  const renderTableHeaders = () => (
    <TableHeader
      headerColor={headerColor || Colors.transparent}
      textColor={textHeaderColor || Colors.white}
      font={empresa?.LETRA_PRINCIPAL}
      key={id}
    >
      <tr>
        {columns.map((column, index) => (
          <React.Fragment key={index}>
            <TableHeaderCell
              key={column.key}
              font={empresa?.LETRA_PRINCIPAL}
              cellColor={cellColor || empresa?.COLOR_PRINCIPAL}
              width={column.width || "auto"}
            >
              {column.renderHeader ? column.renderHeader() : column.title}
            </TableHeaderCell>
            <CellSeparator width={"3px"} />
          </React.Fragment>
        ))}
      </tr>
    </TableHeader>
  );

  const renderTableRows = () =>
    currentItems.map((item, index) => (
      <TableRow key={index}>
        {columns.map((column, columnIndex) => (
          <React.Fragment key={`${column.key}-${index}-${id}`}>
            <TableCell key={column.key} font={empresa?.LETRA_PRINCIPAL}>
              {column.render
                ? column.render(item[column.dataIndex], item, index)
                : item[column.dataIndex]}
            </TableCell>
            <CellSeparator width={"3px"} />
          </React.Fragment>
        ))}
      </TableRow>
    ));

  const renderPagination = () => {
    const safeTotalPages = Math.max(1, Math.floor(totalPages) || 1);

    return (
      <PaginationWrapper font={empresa?.LETRA_PRINCIPAL}>
        {Array.from({ length: safeTotalPages }, (_, index) => (
          <PaginationButton
            key={index}
            active={index + 1 === activePage}
            onClick={() => handlePageChange(index + 1)}
            paginationColor={paginationColor || empresa?.COLOR_PRINCIPAL}
            font={empresa?.LETRA_PRINCIPAL}
          >
            {index + 1}
          </PaginationButton>
        ))}
      </PaginationWrapper>
    );
  };

  return (
    <TableContainer font={empresa?.LETRA_PRINCIPAL}>
      <TableWrapper
        font={empresa?.LETRA_PRINCIPAL}
        width={width}
        height={height}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        minWidth={minWidth}
        minHeight={minHeight}
      >
        <Table font={empresa?.LETRA_PRINCIPAL}>
          <CheckRender allowed={!hideHeader}>
            {renderTableHeaders()}
          </CheckRender>
          <TableBody>{renderTableRows()}</TableBody>
        </Table>
      </TableWrapper>

      <CheckRender allowed={showPagination && totalPages > 1}>
        {renderPagination()}
      </CheckRender>
    </TableContainer>
  );
};

export default CustomTable;
